/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspace } from "../context/WorkspaceContext";
import { Inline } from "@design-system";
import {
  PageLayout,
  InstantSearch,
  EmptyState,
  emptyOrdersIllustration,
  InvoiceList,
  HelpLabel,
  StoryblokHelpBlock,
  SuccessToast,
  ValidationToast,
  MultiSelectFilterDropdown,
  MultiSelectFilterOption,
} from "@components";
import { useTranslation } from "react-i18next";
import { Invoice } from "../models";
import {
  HiOutlineFilter,
  HiOutlineX,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import { useInfiniteInvoices } from "../hooks";
import { useWorkspaceData } from "@data";
import { filterInvoices } from "../helpers/invoiceFilters";
import {
  buildInvoiceExcelData,
  buildInvoiceWorkbook,
  writeInvoiceWorkbook,
} from "../helpers/invoiceExport";
import { ProjectNumberSummaryBanner } from "./invoices/components/ProjectNumberSummaryBanner";
import { ReadyToInvoice } from "../components/InvoiceList/ReadyToInvoice";
import { WorkspaceRole } from "../types";

const Invoices: FC = function () {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingTab, setBillingTab] = useState(false);
  const [splitIsOpen, setSplitIsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | undefined>(
    undefined,
  );
  const { activeWorkspace } = useWorkspace();
  const selectedInvoiceId = searchParams.get("id");
  useEffect(() => {
    setBillingTab(false);
  }, [activeWorkspace?.workspaceId, activeWorkspace?.role, selectedInvoiceId]);
  const { t } = useTranslation(["common", "invoice", "validation"]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const [projectNumberSearch, setProjectNumberSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [accountingStatusFilter, setAccountingStatusFilter] = useState<
    string[]
  >([]);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [accountingStatusDropdownOpen, setAccountingStatusDropdownOpen] =
    useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportSuccess, setShowExportSuccess] = useState(false);
  const [showExportError, setShowExportError] = useState(false);
  const [exportErrorMessage, setExportErrorMessage] = useState<string>("");
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const accountingStatusDropdownRef = useRef<HTMLDivElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const exportAbortRef = useRef(false);
  const isUpdatingFromStateRef = useRef(false);

  const {
    invoices,
    isLoading,
    isLoadingMore,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteInvoices(activeWorkspace?.workspaceId ?? "");

  // Whether any filter/search/export-driving state is active. When it is, the
  // list must reflect the *whole* dataset (client filters, project-number totals
  // and Excel export all operate in-memory), so we eagerly load every page.
  // When idle, the list stays lazily paginated behind the "Load more" button.
  const isFilterActive =
    statusFilter.length > 0 ||
    accountingStatusFilter.length > 0 ||
    companyFilter.length > 0 ||
    searchText.length > 0 ||
    projectNumberSearch.length > 0;

  useEffect(() => {
    if (isFilterActive && hasNextPage && !isLoadingMore) {
      fetchNextPage();
    }
  }, [isFilterActive, hasNextPage, isLoadingMore, fetchNextPage]);

  // Companies for the filter dropdown come from the centrally-loaded store.
  const { companies } = useWorkspaceData();

  // Calculate dropdown position to avoid going off-screen
  const getDropdownPosition = (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return {};

    const rect = ref.current.getBoundingClientRect();
    const dropdownWidth = 300;
    const viewportWidth = window.innerWidth;
    const spaceOnRight = viewportWidth - rect.right;
    const spaceOnLeft = rect.left;

    // If there's not enough space on the right, position it to the right edge
    if (spaceOnRight < dropdownWidth && spaceOnLeft > dropdownWidth) {
      return { right: "0", left: "auto", width: `${dropdownWidth}px` };
    }

    return { left: "0", right: "auto", width: `${dropdownWidth}px` };
  };

  const statusOptions = useMemo(
    () => [
      { value: "draft", label: t("invoice:invoice_status.draft.status") },
      { value: "discarded", label: t("invoice:invoice_status.discarded.status") },
      { value: "sent", label: t("invoice:invoice_status.sent.status") },
      {
        value: "sent_to_accounting",
        label: t("invoice:invoice_status.sent_to_accounting.status"),
      },
      { value: "booked", label: t("invoice:invoice_status.booked.status") },
      {
        value: "finalized",
        label: t("invoice:invoice_status.finalized.status"),
      },
      {
        value: "partially_paid",
        label: t("invoice:invoice_status.partially_paid.status"),
      },
      { value: "paid", label: t("invoice:invoice_status.paid.status") },
      {
        value: "overpaid",
        label: t("invoice:invoice_status.overpaid.status"),
      },
      {
        value: "credited",
        label: t("invoice:invoice_status.credited.status"),
      },
      {
        value: "credit_invoice",
        label: t("invoice:invoice_status.credit_invoice.status"),
      },
    ],
    [t],
  );
  const accountingStatusOptions = useMemo(
    () => [
      {
        value: "manual_registered",
        label: t("invoice:accounting_status.manual_registered.status"),
      },
      {
        value: "not_registered",
        label: t("invoice:accounting_status.not_registered.status"),
      },
      {
        value: "creating",
        label: t("invoice:accounting_status.creating.status"),
      },
      {
        value: "created",
        label: t("invoice:accounting_status.created.status"),
      },
      {
        value: "sync_error",
        label: t("invoice:accounting_status.sync_error.status"),
      },
    ],
    [t],
  );

  // Initialize filters from URL parameters when component mounts
  useEffect(() => {
    // Skip if we're updating from state to prevent loops
    if (isUpdatingFromStateRef.current) {
      isUpdatingFromStateRef.current = false;
      return;
    }

    // Get status filter from URL
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const statuses = statusParam.split(",");
      const validStatuses = statuses.filter((status) =>
        statusOptions.some((option) => option.value === status),
      );

      if (validStatuses.length > 0) {
        setStatusFilter(validStatuses);
      } else {
        setStatusFilter([]);
      }
    } else {
      setStatusFilter([]);
    }
    const accountingParam = searchParams.get("accounting");
    setAccountingStatusFilter(
      accountingParam
        ? accountingParam
            .split(",")
            .filter((status) =>
              accountingStatusOptions.some((option) => option.value === status),
            )
        : [],
    );

    // Get company filter from URL
    const companyParam = searchParams.get("company");
    if (companyParam) {
      const companyIds = companyParam.split(",");
      setCompanyFilter(companyIds);
    } else {
      setCompanyFilter([]);
    }

    // Get search text from URL
    const searchParam = searchParams.get("search");
    setSearchText(searchParam ?? "");

    // Get project number search from URL
    const projectNumberParam = searchParams.get("projectNumber");
    setProjectNumberSearch(projectNumberParam ?? "");
  }, [searchParams, statusOptions, accountingStatusOptions]);

  // Update URL when filters change (but NOT on every keystroke for search)
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    // Preserve the invoice ID if it exists
    const invoiceId = searchParams.get("id");
    if (invoiceId) {
      newSearchParams.set("id", invoiceId);
    }

    // Update status parameter
    if (statusFilter.length > 0) {
      newSearchParams.set("status", statusFilter.join(","));
    }
    if (accountingStatusFilter.length > 0) {
      newSearchParams.set("accounting", accountingStatusFilter.join(","));
    }

    // Update company parameter
    if (companyFilter.length > 0) {
      newSearchParams.set("company", companyFilter.join(","));
    }

    // Keep the shareable search params in the URL contract so that links like
    // /invoices?search=1001 are not stripped on the first sync.
    if (searchText) {
      newSearchParams.set("search", searchText);
    }
    if (projectNumberSearch) {
      newSearchParams.set("projectNumber", projectNumberSearch);
    }

    // Only update URL if different from current to avoid unnecessary re-renders
    const currentParams = searchParams.toString();
    const newParams = newSearchParams.toString();
    if (currentParams !== newParams) {
      // Set flag to prevent URL-to-state sync loop
      isUpdatingFromStateRef.current = true;
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [
    statusFilter,
    accountingStatusFilter,
    companyFilter,
    searchText,
    projectNumberSearch,
    searchParams,
    setSearchParams,
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node) &&
        statusDropdownOpen
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        accountingStatusDropdownRef.current &&
        !accountingStatusDropdownRef.current.contains(event.target as Node) &&
        accountingStatusDropdownOpen
      ) {
        setAccountingStatusDropdownOpen(false);
      }
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node) &&
        companyDropdownOpen
      ) {
        setCompanyDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [statusDropdownOpen, accountingStatusDropdownOpen, companyDropdownOpen]);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleCompanyFilterChange = (companyId: string) => {
    setCompanyFilter((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((c) => c !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };
  const handleAccountingStatusFilterChange = (status: string) => {
    setAccountingStatusFilter((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status],
    );
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setAccountingStatusFilter([]);
    setCompanyFilter([]);
    setSearchText("");
    setProjectNumberSearch("");
  };

  const filteredInvoices = filterInvoices(invoices, {
    statusFilter,
    accountingStatusFilter,
    companyFilter,
    searchText,
    projectNumberSearch,
  });

  const exportToExcel = async () => {
    if (filteredInvoices.length === 0) return;

    // Reset abort flag
    exportAbortRef.current = false;
    setIsExporting(true);
    setShowExportError(false);
    setShowExportSuccess(false);

    try {
      // Check if operation was aborted
      if (exportAbortRef.current) return;

      // Format data for Excel
      const excelData = buildInvoiceExcelData(filteredInvoices, t);

      if (exportAbortRef.current) return;

      // Build workbook + safe filename
      const { workbook, filename } = buildInvoiceWorkbook(
        excelData,
        projectNumberSearch,
        t,
      );

      if (exportAbortRef.current) return;

      // Download file
      writeInvoiceWorkbook(workbook, filename);

      // Show success message
      setShowExportSuccess(true);
      setTimeout(() => setShowExportSuccess(false), 3000);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : t("invoice:export_error_generic");
      setExportErrorMessage(errorMsg);
      setShowExportError(true);
    } finally {
      if (!exportAbortRef.current) {
        setIsExporting(false);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      exportAbortRef.current = true;
    };
  }, []);

  // Calculate total sum and count for filtered invoices when searching by project number
  const projectNumberStats = useMemo(() => {
    if (!projectNumberSearch) return { total: 0, count: 0 };

    const total = filteredInvoices.reduce((sum, invoice) => {
      return sum + (invoice.amounts?.totalAmount ?? 0);
    }, 0);

    return { total, count: filteredInvoices.length };
  }, [filteredInvoices, projectNumberSearch]);

  const statusFilterOptions: MultiSelectFilterOption[] = statusOptions.map(
    (status) => ({
      key: status.value,
      label: status.label,
      selected: statusFilter.includes(status.value),
    }),
  );

  const companyFilterOptions: MultiSelectFilterOption[] = companies.map(
    (company) => ({
      key: company.id,
      label: company.name,
      selected: companyFilter.includes(company.id),
      title: company.name,
    }),
  );
  const accountingFilterOptions: MultiSelectFilterOption[] =
    accountingStatusOptions.map((status) => ({
      key: status.value,
      label: status.label,
      selected: accountingStatusFilter.includes(status.value),
    }));

  return (
    <PageLayout>
      {(activeWorkspace?.role === WorkspaceRole.Owner || activeWorkspace?.role === WorkspaceRole.Administrator) && (
        <div className="mb-4 flex gap-5 border-b border-border" role="tablist" aria-label={t("invoice:page_title")}>
          <button role="tab" aria-selected={!billingTab} className={`pb-3 ${!billingTab ? "border-b-2 border-brand text-brand" : "text-secondary"}`} onClick={() => setBillingTab(false)}>{t("invoice:consolidated.history")}</button>
          <button role="tab" aria-selected={billingTab} className={`pb-3 ${billingTab ? "border-b-2 border-brand text-brand" : "text-secondary"}`} onClick={() => setBillingTab(true)}>{t("invoice:consolidated.ready")}</button>
        </div>
      )}
      {billingTab ? <ReadyToInvoice key={activeWorkspace?.workspaceId} /> : <>
      {/* Success Toast */}
      <SuccessToast
        title={t("invoice:export_success")}
        message={`${filteredInvoices.length} ${filteredInvoices.length === 1 ? t("invoice:invoice_singular") : t("invoice:invoices_plural")}`}
        show={showExportSuccess}
        onClose={() => setShowExportSuccess(false)}
      />

      {/* Error Toast */}
      <ValidationToast
        title={t("invoice:export_error")}
        message={exportErrorMessage}
        show={showExportError}
        onClose={() => setShowExportError(false)}
      />

      {/* Only show the header if there are invoices */}
      {invoices.length > 0 && (
        <div className="flex flex-col">
          <div className={splitIsOpen ? "hidden lg:block" : ""}>
            <Inline gap={3}>
              <h1 className="text-xl font-semibold text-text sm:text-2xl">
                {t("invoice:page_title")}
              </h1>
              <HelpLabel
                content={
                  <StoryblokHelpBlock
                    storySlug="logibud-tms-web/help-popups/invoice"
                    accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                  />
                }
                className="mt-1"
              />
            </Inline>
            <p className="text-secondary text-sm sm:text-base pt-1 sm:pt-2 pb-2 sm:pb-4 dark:text-secondary">
              {t("invoice:page_description")}
            </p>
          </div>

          {/* Search and Filter Bar - Hidden on mobile when invoice details are open */}
          <div
            className={`flex flex-col md:flex-row gap-2 mb-4 ${selectedInvoice ? "hidden lg:flex" : "flex"}`}
          >
            {/* Search Input */}
            <InstantSearch
              placeholder={t("common:search")}
              value={searchText}
              onChange={setSearchText}
            />

            {/* Project Number Search Input - Smaller width */}
            <div className="md:max-w-xs w-full">
              <InstantSearch
                placeholder={t("invoice:search_project_number")}
                value={projectNumberSearch}
                onChange={setProjectNumberSearch}
              />
            </div>

            {/* Status Filter Dropdown */}
            <MultiSelectFilterDropdown
              containerRef={statusDropdownRef}
              isOpen={statusDropdownOpen}
              onToggle={() => setStatusDropdownOpen(!statusDropdownOpen)}
              icon={<HiOutlineFilter className="h-5 w-5" />}
              label={t("common:status")}
              options={statusFilterOptions}
              onOptionClick={(key, e) => {
                e.stopPropagation();
                handleStatusFilterChange(key);
              }}
              headerTitle={t("common:status")}
              selectedCount={statusFilter.length}
              triggerExtraClassName="min-w-[115px]"
              panelStyle={getDropdownPosition(statusDropdownRef)}
              optionButtonExtraClassName="focus:outline-none focus:ring-0"
            />
            <MultiSelectFilterDropdown
              containerRef={accountingStatusDropdownRef}
              isOpen={accountingStatusDropdownOpen}
              onToggle={() =>
                setAccountingStatusDropdownOpen(!accountingStatusDropdownOpen)
              }
              icon={<HiOutlineFilter className="h-5 w-5" />}
              label={t("invoice:accounting_status.filter_label")}
              options={accountingFilterOptions}
              onOptionClick={(key, e) => {
                e.stopPropagation();
                handleAccountingStatusFilterChange(key);
              }}
              headerTitle={t("invoice:accounting_status.filter_label")}
              selectedCount={accountingStatusFilter.length}
              triggerExtraClassName="min-w-[150px]"
              panelStyle={getDropdownPosition(accountingStatusDropdownRef)}
              optionButtonExtraClassName="focus:outline-none focus:ring-0"
            />

            {/* Company Filter Dropdown */}
            <MultiSelectFilterDropdown
              containerRef={companyDropdownRef}
              isOpen={companyDropdownOpen}
              onToggle={() => setCompanyDropdownOpen(!companyDropdownOpen)}
              icon={<HiOutlineOfficeBuilding className="h-5 w-5" />}
              label="Selskap"
              options={companyFilterOptions}
              onOptionClick={(key, e) => {
                e.stopPropagation();
                handleCompanyFilterChange(key);
              }}
              headerTitle="Selskap"
              selectedCount={companyFilter.length}
              triggerExtraClassName="min-w-[115px]"
              panelStyle={getDropdownPosition(companyDropdownRef)}
              optionButtonExtraClassName="focus:outline-none focus:ring-0"
              inputExtraClassName="shrink-0"
              spanClassName="dark:text-white text-left break-words"
            />

            {/* Clear Filters Button */}
            <button
              onClick={clearFilters}
              disabled={
                statusFilter.length === 0 &&
                accountingStatusFilter.length === 0 &&
                companyFilter.length === 0 &&
                !searchText &&
                !projectNumberSearch
              }
              className={`flex items-center justify-center p-2.5 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600
                ${
                  statusFilter.length === 0 &&
                  accountingStatusFilter.length === 0 &&
                  companyFilter.length === 0 &&
                  !searchText &&
                  !projectNumberSearch
                    ? "text-muted dark:text-gray-600 opacity-50 cursor-not-allowed"
                    : "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                }`}
              title={t("common:clear_filters")}
            >
              <HiOutlineX className="h-5 w-5" />
            </button>
          </div>

          {/* Project Number Summary */}
          {projectNumberSearch && (
            <ProjectNumberSummaryBanner
              projectNumberSearch={projectNumberSearch}
              count={projectNumberStats.count}
              total={projectNumberStats.total}
              isExporting={isExporting}
              onExport={exportToExcel}
            />
          )}
        </div>
      )}

      {/* Render empty state if there are no invoices and not loading */}
      {!isLoading && invoices.length === 0 ? (
        <EmptyState
          icon={emptyOrdersIllustration}
          title={t("invoice:empty_state.general.title")}
          description={t("invoice:empty_state.general.description")}
          buttonText={t("invoice:empty_state.general.button_text")}
          buttonClick={() => navigate("/orders/new")}
        />
      ) : (
        <div>
          <InvoiceList
            invoices={filteredInvoices}
            isLoading={isLoading}
            isOpen={setSplitIsOpen}
            onRefresh={() => refetch()}
            onInvoiceSelect={setSelectedInvoice}
            tableFooter={
              !isFilterActive && hasNextPage ? (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isLoadingMore}
                  aria-label={t("common:load_more", "Load more")}
                  className="w-full py-3 text-center text-sm font-medium text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoadingMore
                    ? t("common:loading", "Loading...")
                    : t("common:load_more", "Load more")}
                </button>
              ) : isFilterActive && isLoadingMore ? (
                <p className="py-3 text-center text-sm text-muted">
                  {t("common:loading", "Loading...")}
                </p>
              ) : undefined
            }
          />
        </div>
      )}
      </>}
    </PageLayout>
  );
};

export default Invoices;
